import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAllMakes, getModelsByMake, getMotorcycle, createMotorcycle, updateMotorcycle } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardHeader, CardContent, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ChevronLeft, Save, AlertCircle } from 'lucide-react';

const CONDITIONS = ['New', 'Used', 'Project / Parts Only'];

export default function MotorcycleForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    make: '',
    model: '',
    year: '',
    condition: '',
    mileage: '',
    displacement: '',
    engineType: '',
    colour: '',
    vin: '',
    title: '',
    description: '',
    price: '',
    isAvailable: true,
  });

  const [deleteDialog, setDeleteDialog] = useState(null);

  useEffect(() => {
    setLoading(true);
    getAllMakes()
      .then(makesData => {
        setMakes(makesData);
        if (isEdit) {
          return getMotorcycle(id).then(data => {
            const moto = data.motorcycle || data;
            setForm({
              make: (typeof moto.make === 'object' && moto.make != null && moto.make.name) ? moto.make.name : (moto.make || ''),
              model: moto.model || '',
              year: moto.year?.toString() || '',
              condition: moto.condition || '',
              mileage: moto.mileage?.toString() || '',
              displacement: moto.displacement?.toString() || '',
              engineType: moto.engineType || '',
              colour: moto.colour || '',
              vin: moto.vin || '',
              title: moto.title || '',
              description: moto.description || '',
              price: moto.price?.toString() || '',
              isAvailable: moto.isAvailable ?? true,
            });
          });
        }
      })
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // Load models when make changes
  useEffect(() => {
    if (form.make) {
      getModelsByMake(form.make)
        .then(data => setModels(data.sort()))
        .catch(() => setModels([]));
    } else {
      setModels([]);
    }
  }, [form.make]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.make || !form.model || !form.year || !form.condition || !form.title || !form.price) {
      setError('Please fill in all required fields');
      return;
    }
    setError('');
    setSaving(true);
    try {
      // Auto-generate SKU and slug
      const sku = (form.title.split(' ')[0] + '-' + form.model).toUpperCase().replace(/\s+/g, '-').substring(0, 20);
      const slug = `${form.make}-${form.model}-${form.year}`.toLowerCase().replace(/\s+/g, '-');

      const data = {
        make: form.make,
        model: form.model,
        year: parseInt(form.year),
        condition: form.condition,
        mileage: form.mileage ? parseInt(form.mileage) : null,
        displacement: form.displacement ? parseInt(form.displacement) : null,
        engineType: form.engineType,
        colour: form.colour,
        vin: form.vin || null,
        title: form.title,
        slug: slug,
        description: form.description,
        price: parseFloat(form.price),
        isAvailable: form.isAvailable,
        sku: sku,
      };

      if (isEdit) {
        await updateMotorcycle(id, data);
      } else {
        await createMotorcycle(data);
      }
      navigate('/admin/motorcycles');
    } catch (err) {
      setError(err.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      const { deleteMotorcycle } = await import('../../api');
      await deleteMotorcycle(id);
      navigate('/admin/motorcycles');
    } catch {
      alert('Delete failed');
    } finally {
      setDeleteDialog(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><p>Loading...</p></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/motorcycles')}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-slate-900">{isEdit ? 'Edit Motorcycle' : 'Add Motorcycle'}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/motorcycles')}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
          {isEdit && (
            <Button variant="destructive" size="sm" onClick={() => setDeleteDialog(true)}>Delete</Button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Motorcycle Info</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Make *</Label>
                <Select value={form.make} onValueChange={v => updateField('make', v)}>
                  <SelectTrigger><SelectValue placeholder="Select make" /></SelectTrigger>
                  <SelectContent>
                    {makes.map((m) => {
                      const makeName = typeof m === 'string' ? m : m.name;
                      const makeKey = typeof m === 'string' ? `string-${m}` : `obj-${m._id}`;
                      return <SelectItem key={makeKey} value={makeName}>{makeName}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Model *</Label>
                <Select value={form.model} onValueChange={v => updateField('model', v)} disabled={models.length === 0}>
                  <SelectTrigger><SelectValue placeholder={models.length === 0 ? "Select a make first" : "Select model"} /></SelectTrigger>
                  <SelectContent>
                    {models.map(m => {
                      const modelName = typeof m === 'string' ? m : m.name;
                      return <SelectItem key={modelName} value={modelName}>{modelName}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Year *</Label>
                  <Input type="number" value={form.year} onChange={e => updateField('year', e.target.value)} />
                </div>
                <div>
                  <Label>Condition *</Label>
                  <Select value={form.condition} onValueChange={v => updateField('condition', v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Title *</Label>
                <Input value={form.title} onChange={e => updateField('title', e.target.value)} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Specifications</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Mileage (km)</Label>
                  <Input type="number" value={form.mileage} onChange={e => updateField('mileage', e.target.value)} placeholder="Optional" />
                </div>
                <div>
                  <Label>Displacement (cc)</Label>
                  <Input type="number" value={form.displacement} onChange={e => updateField('displacement', e.target.value)} placeholder="Optional" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Engine Type</Label>
                  <Input value={form.engineType} onChange={e => updateField('engineType', e.target.value)} placeholder="e.g., V-Twin" />
                </div>
                <div>
                  <Label>Colour</Label>
                  <Input value={form.colour} onChange={e => updateField('colour', e.target.value)} />
                </div>
              </div>
              <div>
                <Label>VIN</Label>
                <Input value={form.vin} onChange={e => updateField('vin', e.target.value)} placeholder="Optional" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Pricing & Availability</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Price (CAD) *</Label>
                <Input type="number" step="0.01" value={form.price} onChange={e => updateField('price', e.target.value)} />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="avail" checked={form.isAvailable} onCheckedChange={v => updateField('isAvailable', v)} />
                <Label htmlFor="avail">Available for sale</Label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Description</CardTitle></CardHeader>
            <CardContent>
              <Textarea
                rows={6}
                value={form.description}
                onChange={e => updateField('description', e.target.value)}
                placeholder="Description..."
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Delete Motorcycle
            </DialogTitle>
            <DialogDescription>Are you sure you want to delete this motorcycle?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}